// Planner — Regime configureren (FRONTEND-BLUEPRINT §2.4/§3.3). The per-tenant compliance-regime
// activation surface: the org_activity_config overlay over the canonical activities-library. Canonical
// Activities are grouped by section_code (Section → Activity tree); each row shows activity_code + name,
// an activity_type badge (verwerking/organisatie/beide), a default-on vs opt-in marker, and an
// "Inschakelen" Switch toggling whether the Activity is live for this tenant's regime.
// Backend: GET /regime/activities · GET /regime · POST /regime (api-client listCanonicalActivities /
// getRegime / setActivityEnabled). RLS-scoped; gated verwerking.read / verwerking.write.
import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useSession } from "../../lib/session";
import { hasPermission } from "@sympllex/auth";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";

type ActivityType = "verwerking" | "organisatie" | "beide";
interface CanonicalActivity {
  id: string; name: string; activityType: ActivityType;
  activityCode: string | null; sectionCode: string | null; regulationId: string | null;
}
interface RegimeRow {
  activityId: string; enabled: boolean; isDefault: boolean; assignedUserId: string | null;
}

const errText = (e: unknown) => (e instanceof ApiError ? `${e.status}` : (e as Error).message);
const TYPE_VARIANT: Record<ActivityType, "default" | "secondary" | "outline"> = {
  verwerking: "default", organisatie: "secondary", beide: "outline",
};

export function RegimeRoute() {
  const { t } = useI18n();
  const session = useSession();
  const canWrite = hasPermission(session, "verwerking.write");
  const qc = useQueryClient();

  const activitiesQ = useQuery({ queryKey: ["regime", "activities"], queryFn: () => api.listCanonicalActivities() as Promise<CanonicalActivity[]> });
  const regimeQ = useQuery({ queryKey: ["regime", "overlay"], queryFn: () => api.getRegime() as Promise<RegimeRow[]> });

  const toggle = useMutation({
    mutationFn: ({ activityId, enabled }: { activityId: string; enabled: boolean }) => api.setActivityEnabled(activityId, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regime", "overlay"] }),
  });

  const overlay = useMemo(() => {
    const m = new Map<string, RegimeRow>();
    for (const r of regimeQ.data ?? []) m.set(r.activityId, r);
    return m;
  }, [regimeQ.data]);

  const loading = activitiesQ.isLoading || regimeQ.isLoading;
  const error = activitiesQ.error ?? regimeQ.error;
  const activities = activitiesQ.data ?? [];

  // The three regime views (FRONTEND-BLUEPRINT §3.3): Alle · Ingeschakelde · Mijn activiteiten.
  const enabledIds = useMemo(() => new Set((regimeQ.data ?? []).filter((r) => r.enabled).map((r) => r.activityId)), [regimeQ.data]);
  const mineIds = useMemo(() => new Set((regimeQ.data ?? []).filter((r) => r.assignedUserId === session.user.id).map((r) => r.activityId)), [regimeQ.data, session.user.id]);

  const enabledActivities = useMemo(() => activities.filter((a) => enabledIds.has(a.id)), [activities, enabledIds]);
  const mineActivities = useMemo(() => activities.filter((a) => mineIds.has(a.id)), [activities, mineIds]);

  return (
    <section>
      <PageHeader title={t("regime.title")} description={t("regime.sub")} />
      {error && <p className="mb-4 text-sm text-destructive">{t("regime.failed")} {errText(error)}</p>}
      {toggle.isError && <p className="mb-4 text-sm text-destructive">{t("regime.saveFailed")} {errText(toggle.error)}</p>}

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">{t("regime.tab.all")}</TabsTrigger>
          <TabsTrigger value="enabled">{t("regime.tab.enabled")}</TabsTrigger>
          <TabsTrigger value="mine">{t("regime.tab.mine")}</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <RegimeList loading={loading} activities={activities} overlay={overlay}
            emptyKey="regime.empty" canWrite={canWrite} pending={toggle.isPending}
            onToggle={(activityId, enabled) => toggle.mutate({ activityId, enabled })} />
        </TabsContent>
        <TabsContent value="enabled">
          <RegimeList loading={loading} activities={enabledActivities} overlay={overlay}
            emptyKey="regime.empty.enabled" canWrite={canWrite} pending={toggle.isPending}
            onToggle={(activityId, enabled) => toggle.mutate({ activityId, enabled })} />
        </TabsContent>
        <TabsContent value="mine">
          <RegimeList loading={loading} activities={mineActivities} overlay={overlay}
            emptyKey="regime.empty.mine" canWrite={canWrite} pending={toggle.isPending}
            onToggle={(activityId, enabled) => toggle.mutate({ activityId, enabled })} />
        </TabsContent>
      </Tabs>
    </section>
  );
}

// Group activities by section_code → an ordered list of { section, rows } the Card tree renders.
function groupBySection(activities: CanonicalActivity[]): { section: string; rows: CanonicalActivity[] }[] {
  const groups = new Map<string, CanonicalActivity[]>();
  for (const a of activities) {
    const key = a.sectionCode ?? "—";
    const bucket = groups.get(key);
    if (bucket) bucket.push(a); else groups.set(key, [a]);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
    .map(([section, rows]) => ({ section, rows }));
}

function RegimeList({
  loading, activities, overlay, emptyKey, canWrite, pending, onToggle,
}: {
  loading: boolean;
  activities: CanonicalActivity[];
  overlay: Map<string, RegimeRow>;
  emptyKey: string;
  canWrite: boolean;
  pending: boolean;
  onToggle: (activityId: string, enabled: boolean) => void;
}) {
  const { t } = useI18n();
  const sections = useMemo(() => groupBySection(activities), [activities]);

  if (loading) {
    return <div className="space-y-3 pt-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  }
  if (activities.length === 0) {
    return <div className="mt-4 rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">{t(emptyKey)}</div>;
  }

  return (
    <div className="space-y-4 pt-4">
      {sections.map(({ section, rows }) => (
        <Card key={section}>
          <CardHeader>
            <CardTitle className="text-base">
              <span className="text-muted-foreground">{t("regime.section")} {section}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {rows.map((a) => {
              const row = overlay.get(a.id);
              const enabled = row?.enabled ?? false;
              const isDefault = row?.isDefault ?? false;
              return (
                <div key={a.id} className="flex items-center justify-between gap-4 border-b py-2.5 last:border-b-0">
                  <Link
                    to="/activities/regime/$activityId"
                    params={{ activityId: a.id }}
                    className="flex min-w-0 items-center gap-3 text-left hover:underline"
                  >
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">{a.activityCode ?? "—"}</span>
                    <span className="truncate text-sm font-medium">{a.name}</span>
                  </Link>
                  <div className="flex shrink-0 items-center gap-3">
                    <Badge variant={TYPE_VARIANT[a.activityType]}>{t(`regime.type.${a.activityType}`)}</Badge>
                    <Badge variant={isDefault ? "outline" : "ghost"} className="hidden sm:inline-flex">
                      {isDefault ? t("regime.default.on") : t("regime.default.optin")}
                    </Badge>
                    <Switch
                      checked={enabled}
                      disabled={!canWrite || pending}
                      onCheckedChange={(v) => onToggle(a.id, v)}
                      aria-label={`${t("regime.col.enable")} ${a.activityCode ?? a.name}`}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
